import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { useEffect, useState } from 'react'

import { spritesQuery, type SpriteAsset } from '@/api/sprites'
import { Button } from '@/components/ui/button'
import { Field, Input } from '@/components/ui/input'
import { CastTray, MAX_CAST, PickTile, TILE_GRID, type CastPick, type CastPickerProps } from '@/features/story/CastPicker'

export function pickFromSprite(sprite: SpriteAsset): CastPick {
  return { uid: sprite.uid, name: sprite.name, thumb: sprite.thumb_url, kind: 'person', speaks: true, learning: false }
}

export function SpriteCastPicker({ picks, onToggle, max = MAX_CAST }: CastPickerProps) {
  const [search, setSearch] = useState('')
  const [query, setQuery] = useState('')
  useEffect(() => {
    const timer = setTimeout(() => setQuery(search.trim()), 250)
    return () => clearTimeout(timer)
  }, [search])
  const sprites = useQuery(spritesQuery({ q: query, ready: true, limit: 500 }))
  const picked = new Set(picks.map((p) => p.uid))
  const items = (sprites.data?.items ?? []).filter((sprite) => sprite.ready)

  return (
    <div className="flex flex-col gap-4" data-testid="sprite-cast-picker">
      <CastTray picks={picks} onToggle={onToggle} max={max} />
      <Field label="Search sprite characters" htmlFor="sprite-cast-search" className="max-w-sm">
        <Input id="sprite-cast-search" type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Find a character…" />
      </Field>
      {sprites.isPending ? <p role="status" className="text-sm text-ink-2">Loading sprite characters…</p> : sprites.isError ? (
        <div role="alert" className="flex flex-wrap items-center gap-3 text-sm text-danger">
          <p>Sprite characters could not be loaded.</p>
          <Button variant="secondary" onClick={() => void sprites.refetch()}>Try again</Button>
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-line-strong p-5" role="status">
          <p className="font-medium text-ink">{query ? `No ready sprite characters match “${query}”.` : 'No sprite characters are ready yet.'}</p>
          <p className="mt-1 text-sm text-ink-2">Open a character in the sprite editor and prepare its animation clips, then return to choose it.</p>
          <Button asChild variant="secondary" className="mt-3"><Link to="/sprites">Open sprite editor</Link></Button>
        </div>
      ) : (
        <ul className={TILE_GRID} aria-label="Ready sprite characters">
          {items.map((sprite) => (
            <PickTile key={sprite.uid} pick={pickFromSprite(sprite)} picked={picked.has(sprite.uid)} disabled={picks.length >= max && !picked.has(sprite.uid)} note={`${sprite.clips} ${sprite.clips === 1 ? 'clip' : 'clips'}`} onToggle={onToggle} />
          ))}
        </ul>
      )}
    </div>
  )
}
